import { navigateTo, useRouter } from "nuxt/app";
import {
  UserCenterComponent,
  type UserCenterCompToSubComp,
} from "~/types/user";
import { useAccountStore } from "~/store/account";
import { NUXT_TEXTING } from "~/types/env";
import { useAccountStore } from "~/store/account";

/** 通过路由获取当前页面的user_id */
export const getUserId: () => string = () => {
  const route = useRoute();
  const { user_id } = route.params;
  return user_id as string;
};

/** 当前用户id */
export const getCurrentUserId = () => {
  const accountStore = useAccountStore();
  const { user_id } = storeToRefs(accountStore);
  return user_id.value;
};

/** 当前用户id */
export const getCurrentUuId = () => {
  const accountStore = useAccountStore();
  const { uuid } = storeToRefs(accountStore);
  return uuid.value;
};

export const toEditUpload = (id: string) => {
  navigateTo(`/editUpload/${id}`);
};

export const toPublishPage = () => {
  navigateTo({
    path: `/${getCurrentUserId()}/home`,
    query: { component: UserCenterComponent.UserPublish },
    replace: true,
  });
};

export const toUserHomePage = (userId: string) => {
  if (!userId) return;
  navigateTo(`/${userId}/home?component=${UserCenterComponent.UserHome}`);
};

// 是否在个人中心收藏Tab页了
export const isUserCollectPage = () => {
  const route = useRoute();
  const { component } = route.query;
  console.log("component:", component);
  return component && component === UserCenterComponent.UserCollect;
};

export const toModelDetail = (modelId: string) => {
  navigateTo(`/models/${modelId}`);
};

export const toModelDetailByReplace = (modelId: string) => {
  navigateTo({ path: `/models/${modelId}`, replace: true });
};

export const toPrintConfigDetail = (modelId: string, printConfigId: string) => {
  navigateTo(`/models/${modelId}?printConfigId=${printConfigId}`);
};

/**
 * 跳转到个人中心，默认跳转到home页
 * @param tabName 需要具体跳转到的某个tab页，不传则默认跳转到home页
 */
export const toUserCenter = (
  tabName?: UserCenterComponent,
  subComponent?: string,
  handlerKey?: UserCenterSettingHandlerKey
) => {
  const baseUrl = `/${getCurrentUserId()}/home`;
  let url = tabName ? `${baseUrl}?component=${tabName}` : baseUrl;
  if (subComponent) {
    url += `&subComponent=${subComponent}`;
  }
  navigateTo(url);
};

/**
 * 在个人中心里进行的Tab路由跳转 --支持访问自己/或他人的某个Tab
 */
export const jumpInUserCenter = (
  tabName?: UserCenterComponent,
  subComponent?: string
) => {
  if (!tabName) return;
  const router = useRouter();
  navigateTo({
    path: router.currentRoute.value.path,
    query: { component: tabName?.toString(), subComponent },
  });
};

const needLoginPath = [
  "upload",
  "uploadPrintConf-model_id",
  "editUploadPrintConf-setting_id",
  "editUpload-model_id",
  "editUploadPrintConf-model_id",
  "messages-system-notifications",
  "messages-following-feed",
  "messages-replies-mentions",
  "messages-notifications-preferences",
  "carry-ling-accounts",
  "carry-select-model",
  "carry-import-model",
];

// const regex = /\/(.*)/;
export const matchneedLoginPath = (routeName: string) => {
  if (!routeName) return false;
  return needLoginPath.includes(routeName);
};

// 跳转到添加打印配置页面
export const toAddPrintConfigPage = (modelId: string) => {
  if (!modelId) return;
  navigateTo({
    path: `/uploadPrintConf/${modelId}`,
  });
};

// 跳转打印配置编辑
export const printEdit = (data) => {
  if (!data || !data.id) return;
  navigateTo({
    path: `/editUploadPrintConf/${data.id}`,
  });
};

// 主页内跳转Tab页
export const homeRouterHandler = (component: UserCenterComponent) => {
  const router = useRouter();
  if (!router.currentRoute || !component) return;
  router.replace({
    path: router.currentRoute.path,
    query: { component: component.toString() },
  });
};

/**
 * 跳转至 个人中心下的 子组件
 * @param tabName 需要跳转到的tab页
 * @param subCompName 需要跳转到的子组件
 * @param isRecord 是否需要记录历史记录，默认为true
 */
export const toUserCenterSubComp = <T extends keyof UserCenterCompToSubComp>(
  tabName: UserCenterComponent,
  subCompName: UserCenterCompToSubComp[T],
  isRecord = true
) => {
  if (isRecord) {
    navigateTo(
      `/${getCurrentUserId()}/home?component=${tabName}&subComp=${subCompName}`
    );
    return;
  }
  const router = useRouter();
  if (!router.currentRoute) return;
  router.replace({
    path: router.currentRoute.path,
    query: { component: tabName.toString(), subComp: subCompName?.toString() },
  });
};

/**
 * 跳转至 个人中心下的 子组件
 * @param tabName 需要跳转到的tab页
 * @param subCompName 需要跳转到的子组件
 * @param collectionId 携带的参数
 */
export const toUserCenterCollectionDetail = <
  T extends keyof UserCenterCompToSubComp
>(
  tabName: T,
  subCompName: UserCenterCompToSubComp[T],
  collectionId: string
) => {
  const router = useRouter();
  if (!router.currentRoute) return;
  router.replace({
    path: router.currentRoute.path,
    query: {
      component: tabName.toString(),
      subComp: subCompName?.toString(),
      collectionId: collectionId?.toString(),
    },
  });
};

/**
 * 清除当前路由中的subComp和collectionId
 */
export const removeQueryParams = () => {
  const router = useRouter();
  const route = useRoute();
  router.replace({
    path: route.path,
    query: {
      ...route.query,
      subComp: undefined, // 设为 undefined 会被自动过滤
      collectionId: undefined,
    },
  });
};

// 不需 KEEPALIVE 的路由;
export const UN_KEEPALIVE_ROUTES = [
  "/account/login",
  "/account/register",
  "/account/password",
];

export const backToLogin = () => {
  navigateTo("/account/login");
};
export const backToHome = () => {
  navigateTo("/");
};

// 模型详情页面增加打印配置ID参数
export const modelPageRouteHandler = (printConfigId: string) => {
  const router = useRouter();
  const route = useRoute();
  if (!router.currentRoute) return;
  router.replace({
    path: router.currentRoute.path,
    query: {
      ...route.query,
      printConfigId,
    },
  });
};

// 获取模型详情页面的打印配置ID
export const getPrintConfigId = () => {
  const route = useRoute();
  const { printConfigId } = route.query;
  return printConfigId;
};

const accountPageList = [
  "account-login",
  "account-register",
  "account-password",
  "account-google",
  "terms-pathName",
];
export const routeProcessingDuringTest = (routeName: string) => {
  if (!routeName) return;
  const route = useRoute();
  if (
    !NUXT_TEXTING ||
    accountPageList.includes(routeName) ||
    route.query.hidden
  ) {
    return;
  }
  console.log("DuringTest");
  // const router = useRouter();
  // router.replace("/account/login");
  useAccountStore().toLoginReplaceWithPath(route.fullPath);
};
